import { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import type { QueryFieldFilterConstraint } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { STATUS_ACTIVE, STATUS_INACTIVE, STATUS_BLOCKED, STATUS_REJECTED } from "../constants";
import { formatError } from "../utils/formatError";
import { fromDoc } from "../utils/firestoreDoc";
import logger from "../utils/logger";
import type { User } from "../types";

interface UseUsersOptions {
  activeOnly?: boolean;
}

interface CreateOfficerArgs {
  name: string;
  email: string;
  badge: string;
  phone: string;
}

export function useUsers(enabled = true, agencyId: string | null = null, options: UseUsersOptions = {}) {
  const { activeOnly = false } = options;
  const [users, setUsers] = useState<User[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Derived: loading iff we have valid deps and haven't received a snapshot for
  // the current (agencyId, activeOnly) tuple yet.
  const depsKey = `${agencyId || ""}:${activeOnly ? "active" : "all"}`;
  const loading = enabled && !!agencyId && loadedFor !== depsKey;

  useEffect(() => {
    if (!enabled || !agencyId) return;
    setError(null);
    // Officers only need names of active fellow officers (for signup avatars).
    // Skipping pending/inactive/blocked users avoids leaking PII and cuts reads.
    const constraints: QueryFieldFilterConstraint[] = [where("agencyId", "==", agencyId)];
    if (activeOnly) constraints.push(where("status", "==", "active"));
    const q = query(collection(db, "users"), ...constraints);
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setUsers(snapshot.docs.map((d) => fromDoc<User>(d)));
        setLoadedFor(depsKey);
      },
      (err) => {
        logger.error("Failed to load users:", err);
        setError(formatError(err));
        setLoadedFor(depsKey);
      }
    );
    return () => unsubscribe();
  }, [enabled, agencyId, activeOnly, depsKey]);

  const approveOfficer = useCallback(async (userId: string) => {
    await updateDoc(doc(db, "users", userId), { status: STATUS_ACTIVE });
  }, []);

  const rejectOfficer = useCallback(async (userId: string) => {
    await updateDoc(doc(db, "users", userId), { status: STATUS_REJECTED });
  }, []);

  const inactivateOfficer = useCallback(async (userId: string) => {
    await updateDoc(doc(db, "users", userId), { status: STATUS_INACTIVE });
  }, []);

  const blockOfficer = useCallback(async (userId: string) => {
    await updateDoc(doc(db, "users", userId), { status: STATUS_BLOCKED });
  }, []);

  const activateOfficer = useCallback(async (userId: string) => {
    await updateDoc(doc(db, "users", userId), { status: STATUS_ACTIVE });
  }, []);

  const deleteOfficer = useCallback(async (userId: string) => {
    await deleteDoc(doc(db, "users", userId));
  }, []);

  const updateProfile = useCallback(async (userId: string, updates: Partial<User>) => {
    await updateDoc(doc(db, "users", userId), updates);
  }, []);

  const createOfficer = useCallback(async ({ name, email, badge, phone }: CreateOfficerArgs) => {
    try {
      const functions = getFunctions();
      const createOfficerAccount = httpsCallable(functions, "createOfficerAccount");
      const result = await createOfficerAccount({ name, email, badge, phone });
      return result.data;
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  return {
    users,
    loading,
    error,
    approveOfficer,
    rejectOfficer,
    inactivateOfficer,
    blockOfficer,
    activateOfficer,
    deleteOfficer,
    updateProfile,
    createOfficer,
  };
}
